import { useState, useEffect, useRef } from 'react'

const AltTabSwitcher = ({ 
  isVisible, 
  windows, 
  selectedIndex, 
  onSelectionChange, 
  onConfirm, 
  onCancel 
}) => {
  const containerRef = useRef(null)

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return

    const handleKeyDown = (e) => {
      if (e.key === 'Tab' && e.altKey) {
        e.preventDefault()
        if (e.shiftKey) {
          // Alt+Shift+Tab - previous window
          onSelectionChange('previous')
        } else {
          // Alt+Tab - next window
          onSelectionChange('next')
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }

    const handleKeyUp = (e) => {
      // When Alt is released, confirm selection
      if (e.key === 'Alt' && isVisible) {
        e.preventDefault()
        onConfirm()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [isVisible, onSelectionChange, onConfirm, onCancel])

  // Focus management
  useEffect(() => {
    if (isVisible && containerRef.current) {
      containerRef.current.focus()
    }
  }, [isVisible])

  if (!isVisible || windows.length === 0) return null

  // Get app icon for window
  const getAppIcon = (appName) => {
    const iconMap = {
      'crumb': '💬',
      'settings': '⚙️',
      'files': '📁',
      'calculator': '🔢',
      'notepad': '📝',
      'browser': '🌐',
      'mail': '📧',
      'music': '🎵',
      'photos': '🖼️',
      'apps': '📱',
      'games': '🎮',
      'colorpicker': '🎨'
    }
    return iconMap[appName] || '📄'
  }

  // Get app display name
  const getAppDisplayName = (appName) => {
    const nameMap = {
      'crumb': 'Crumb AI',
      'settings': 'Settings',
      'files': 'File Manager',
      'calculator': 'Calculator',
      'notepad': 'Notepad',
      'browser': 'Browser',
      'mail': 'Mail',
      'music': 'Music',
      'photos': 'Photos',
      'apps': 'Applications',
      'games': 'Games',
      'colorpicker': 'Color Picker'
    }
    return nameMap[appName] || appName.charAt(0).toUpperCase() + appName.slice(1)
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-md animate-fade-in" />
      
      {/* Alt+Tab switcher container */}
      <div 
        ref={containerRef}
        className="relative pointer-events-auto animate-zoom-in"
        tabIndex={-1}
      >
        <div className="bg-gray-900/95 backdrop-blur-xl border border-gray-600/50 rounded-2xl p-6 shadow-2xl min-w-[450px] max-w-[900px] transform transition-all duration-200">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="text-2xl">⚡</div>
            <div>
              <h3 className="text-white font-semibold text-lg">Alt + Tab</h3>
              <p className="text-gray-400 text-sm">Switch between windows</p>
            </div>
          </div>

          {/* Window list */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {windows.map((window, index) => (
              <div
                key={window.id}
                className={`
                  flex items-center gap-4 p-3 rounded-xl transition-all duration-150
                  ${index === selectedIndex 
                    ? 'bg-blue-600/30 border border-blue-400/50 shadow-lg scale-[1.02]' 
                    : 'bg-gray-800/50 border border-transparent hover:bg-gray-700/50'
                  }
                `}
              >
                {/* App icon */}
                <div className="flex-shrink-0">
                  <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center text-2xl
                    ${index === selectedIndex 
                      ? 'bg-blue-500/20 border border-blue-400/30' 
                      : 'bg-gray-700/50'
                    }
                  `}>
                    {getAppIcon(window.appName)}
                  </div>
                </div>

                {/* Window info */}
                <div className="flex-1 min-w-0">
                  <h4 className={`
                    font-semibold truncate
                    ${index === selectedIndex ? 'text-white' : 'text-gray-200'}
                  `}>
                    {getAppDisplayName(window.appName)}
                  </h4>
                  <p className="text-gray-400 text-sm truncate">
                    {window.isMinimized ? 'Minimized' : 'Active'}
                    {window.isMaximized && !window.isMinimized ? ' • Maximized' : ''}
                  </p>
                </div>

                {/* Window preview placeholder */}
                <div className="flex-shrink-0">
                  <div className={`
                    w-16 h-12 rounded border-2 flex items-center justify-center text-xs
                    ${index === selectedIndex 
                      ? 'border-blue-400/50 bg-blue-500/10 text-blue-300' 
                      : 'border-gray-600 bg-gray-800/50 text-gray-500'
                    }
                  `}>
                    Preview
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <div className="flex items-center gap-4">
                <span><kbd className="bg-gray-700 px-2 py-1 rounded">Tab</kbd> Next</span>
                <span><kbd className="bg-gray-700 px-2 py-1 rounded">Shift+Tab</kbd> Previous</span>
              </div>
              <div className="flex items-center gap-4">
                <span><kbd className="bg-gray-700 px-2 py-1 rounded">Alt ↑</kbd> Select</span>
                <span><kbd className="bg-gray-700 px-2 py-1 rounded">Esc</kbd> Cancel</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AltTabSwitcher
